import React from "react";
import 'react-confirm-alert/src/react-confirm-alert.css';

import { Box, Button, Collapse, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { ApiError } from "../../Api/ApiError";
import { checkDatabase, checkInitData, checkSystemAdmin, createDatabase, createSystemAdmin, setInitData } from "../../Api/System";
import { Link } from "react-router-dom";

const InitializePage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar()

    const [isInitDatabase, setIsInitDatabase] = React.useState<boolean>()
    const [isInitData, setIsInitData] = React.useState<boolean>()
    const [isCreateAdmin, setIsCreateAdmin] = React.useState<boolean>()

    const passRef = React.useRef<HTMLInputElement>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    React.useEffect(() => {
        setIsInitDatabase(undefined)
        checkDatabase({ signal: abortController.signal }).then(result => {
            switch (result) {
                case "NO_DATABASE":
                    setIsInitDatabase(false)
                    break;
                case "READY":
                    setIsInitDatabase(true)
                    break;
            }
        }).catch(e => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, abortController])

    React.useEffect(() => {
        if (isInitDatabase) {
            setIsInitData(undefined)
            checkInitData({ signal: abortController.signal }).then(result => {
                switch (result) {
                    case "NO_DATA":
                        setIsInitData(false)
                        break;
                    case "NO_DATABASE":
                        setIsInitDatabase(false)
                        break;
                    case "READY":
                        setIsInitData(true)
                        break;
                }
            }).catch(e => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [enqueueSnackbar, abortController, isInitDatabase])

    React.useEffect(() => {
        if (isInitData) {
            setIsCreateAdmin(undefined)
            checkSystemAdmin({ signal: abortController.signal }).then(result => {
                switch (result) {
                    case "NO_SYSTEMADMIN":
                        setIsCreateAdmin(false)
                        break;
                    case "NO_DATA":
                        setIsInitData(false)
                        break;
                    case "NO_DATABASE":
                        setIsInitDatabase(false)
                        setIsInitData(undefined)
                        break;
                    case "READY":
                        setIsCreateAdmin(true)
                        break;
                }
            }).catch(e => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [enqueueSnackbar, abortController, isInitData])

    const doInitDatabase = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        createDatabase({ signal: abortController.signal }).then(result => {
            switch (result) {
                case "SUCCESS":
                    enqueueSnackbar("データベースの準備が完了しました", { variant: "success" })
                    setIsInitDatabase(true)
                    break;
                case "ALREADY_EXISTS":
                    setIsInitDatabase(true)
                    break;
            }
        }).catch(e => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, abortController])

    const doInitData = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        setInitData({ signal: abortController.signal }).then(result => {
            switch (result) {
                case "SUCCESS":
                    enqueueSnackbar("初期データの作成が完了しました", { variant: "success" })
                    setIsInitData(true)
                    break;
                case "ALREADY_INITIALIZED":
                    setIsInitData(true)
                    break;
            }
        }).catch(e => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, abortController])

    const doCreateAdmin = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const data = new FormData(e.currentTarget)
        const name: string = data.get('name')?.toString() ?? ""
        const login_str: string = data.get('login_str')?.toString() ?? ""
        const password: string = data.get('password')?.toString() ?? ""
        const pass_confirm: string = data.get('pass_confirm')?.toString() ?? ""

        if (password !== pass_confirm) {
            enqueueSnackbar("パスワードが一致しません", { variant: "error" })
            if (passRef.current) {
                passRef.current.select()
            }
        }

        createSystemAdmin({
            name: name,
            login_str: login_str,
            password: password
        }, { signal: abortController.signal }).then(result => {
            switch (result) {
                case "SUCCESS":
                    enqueueSnackbar("システム管理者の作成が完了しました", { variant: "success" })
                    setIsCreateAdmin(true)
                    break;
                case "NO_DATA":
                    setIsInitData(false)
                    setIsCreateAdmin(undefined)
                    break;
                case "NO_DATABASE":
                    setIsInitDatabase(false)
                    setIsInitData(undefined)
                    setIsCreateAdmin(undefined)
                    break;
            }
        }).catch(e => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, abortController])

    return (
        <Paper
            sx={{
                p: 2
            }}
        >
            <Typography variant="h1">初期化</Typography>
            <Divider />
            <Stack
                spacing={2}
                my={2}
                mx={"auto"}
                maxWidth="sm"
            >
                <Box>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={!(isInitDatabase === false)}
                        onClick={doInitDatabase}
                    >1. データベースの準備</Button>
                </Box>
                <Divider />
                <Box>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={!(isInitData === false)}
                        onClick={doInitData}
                    >2. 初期データの作成</Button>
                </Box>
                <Divider />
                <Box
                    component={"form"}
                    onSubmit={doCreateAdmin}
                >
                    <Collapse
                        in={isCreateAdmin === false}
                    >
                        <Stack
                            spacing={2}
                            pb={4}
                            px={{
                                xs: 0,
                                sm: 2,
                                md: 4,
                            }}
                        >
                            <TextField
                                size="small"
                                name="name"
                                label="表示名"
                                helperText="【必須】表示に使用するスタッフ表示名を入力してください 2〜32文字の文字列が設定可能です。"
                                required
                                inputProps={{
                                    minLength: 2,
                                    maxLength: 32
                                }}
                            />
                            <TextField
                                size="small"
                                name="login_str"
                                label="ログイン名"
                                helperText="【必須】ログイン時に使用するログイン名を入力してください 半角英数字を使用した4〜24文字が設定可能です。"
                                required
                                inputProps={{
                                    minLength: 4,
                                    maxLength: 24
                                }}
                            />
                            <TextField
                                inputRef={passRef}
                                size="small"
                                name="password"
                                type="password"
                                label="ログインパスワード"
                                helperText="【必須】ログイン時に使用するパスワードを入力してください 8〜24文字が設定可能で、半角英数字とハイフン、アンダーバー、が利用可能です。"
                                required
                                inputProps={{
                                    minLength: 8,
                                    maxLength: 24
                                }}
                            />
                            <TextField
                                size="small"
                                name="pass_confirm"
                                type="password"
                                label="ログインパスワード(確認)"
                                required
                                inputProps={{
                                    minLength: 8,
                                    maxLength: 24
                                }}
                            />
                        </Stack>
                    </Collapse>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={!(isCreateAdmin === false)}
                        type="submit"
                    >3. システム管理者の作成</Button>
                </Box>
                <Collapse
                    in={isCreateAdmin}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            textAlign: "center"
                        }}
                    >
                        <Typography>初期化が完了しました。</Typography>
                        <Typography>引き続き管理画面で設定を行ってください</Typography>
                        <Link to={"/login"}>ログイン</Link>
                    </Paper>
                </Collapse>
            </Stack>
        </Paper>
    )
}

export default InitializePage